import BulkActionsDrawerItem from "components/BulkActions/Drawer/Item";
import BulkActionsDrawerItemDismiss from "components/BulkActions/Drawer/Item/Dismiss";
import BulkActionsDrawerItemLink from "components/BulkActions/Drawer/Item/Link";
import {
  BulkActionActionConfirmationTabs,
  BulkActionItemID,
  BulkActionsActionDescriptor,
  BulkActionsStep,
} from "components/BulkActions/types";
import Box from "ds/components/Box";
import useAnalytics from "hooks/useAnalytics";
import { ActionAvailabilityBanner } from "components/BulkActions/Drawer/Item/ActionAvailabiltyBanner";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { Space } from "components/icons/generated";
import Link from "ds/components/Link";
import Typography from "ds/components/Typography";
import { SpaceAccessLevel } from "types/generated";
import TextEllipsis from "ds/components/TextEllipsis";
import BadgeNext from "ds/components/BadgeNext";
import { StackRoleBindingView } from "shared/Stack/Roles/types";

import { StackRolesBulkActions } from "./types";

type StackRolesBulkActionsSelectedItemPropsBase = {
  item: StackRoleBindingView;
  onDismiss: (id: BulkActionItemID) => void;
};

type StackRolesBulkActionsSelectedItemProps = StackRolesBulkActionsSelectedItemPropsBase &
  (
    | {
        step: BulkActionsStep.ActionConfirmation;
        itemCategory: BulkActionActionConfirmationTabs;
        itemActions?: never;
        allActionsQty?: never;
      }
    | {
        step: BulkActionsStep.ChooseAction;
        itemActions: BulkActionsActionDescriptor<StackRolesBulkActions>[] | undefined;
        allActionsQty: number;
        itemCategory?: never;
      }
  );

const StackRolesBulkActionsSelectedItem = ({
  item,
  itemActions,
  onDismiss,
  allActionsQty,
  step,
  itemCategory,
}: StackRolesBulkActionsSelectedItemProps) => {
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageStack.StackSettingsRoles,
  });
  const hasAccessToSpace = item.space.accessLevel !== SpaceAccessLevel.None;

  const handleDismiss = () => {
    onDismiss(item.id);
    trackSegmentAnalyticsEvent("Bulk Actions - Drawer - Item dismissed");
  };

  const isChooseActionStep = step === BulkActionsStep.ChooseAction;
  const actionTexts = (itemActions || []).map((action) => action.title);

  return (
    <BulkActionsDrawerItem key={item.id} direction="column" align="start">
      <Box gap="medium" align="start" justify="between" fullWidth zeroMinWidth={false}>
        <Box>
          <Box gap="large" direction="column" grow="1" fullWidth>
            {item.role.isSystem ? (
              <Box align="center" fullWidth gap="medium">
                <TextEllipsis tooltip={item.role.name} tooltipWidthMode="maxWidthSm">
                  {(props) => (
                    <Typography {...props} tag="p" variant="p-body3">
                      {item.role.name}
                    </Typography>
                  )}
                </TextEllipsis>

                <BadgeNext type="strong" variant="blue" text="SYSTEM" />
              </Box>
            ) : (
              <BulkActionsDrawerItemLink
                link={`/settings/role/${item.role.id}`}
                text={item.role.name || ""}
                step={step}
                itemCategory={itemCategory}
              />
            )}

            <Box align="center" zeroMinWidth>
              <Link
                href={`/spaces/${item.space.id}`}
                startIcon={Space}
                variant="primary"
                disabled={!hasAccessToSpace}
                tooltip={!hasAccessToSpace ? "You don't have access to this space" : undefined}
              >
                {item.space.name}
              </Link>
            </Box>
          </Box>
        </Box>

        <BulkActionsDrawerItemDismiss onDismiss={handleDismiss} />
      </Box>

      {isChooseActionStep && (
        <ActionAvailabilityBanner itemActions={actionTexts} allActionsQty={allActionsQty} />
      )}
    </BulkActionsDrawerItem>
  );
};

export default StackRolesBulkActionsSelectedItem;
